import {Popover} from '@dropbox/dig-components/popover';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Text} from '@dropbox/dig-components/typography';
import {Stack} from '@dropbox/dig-foundations';
import {Link} from 'components/DSYS/Link';
import {t} from 'i18next';
import {useState} from 'react';
import {useWorkstream} from 'views/workstreams/hooks';
import {getWorkstreamDetailPath} from 'views/workstreams/routeUtils';

import styles from './WorkstreamTable.module.css';

// Rows mount only while the popover is open, so names are fetched on demand (and the
// detail is warm if the viewer clicks through). Restricted workstreams come back as "Restricted".
const SharedMetricWorkstreamRow = ({id, isCurrent}: {id: number; isCurrent: boolean}) => {
  const {workstream, isLoading} = useWorkstream(id);

  if (isLoading) {
    return <Skeleton.Text width={160} />;
  }

  const name = workstream?.name ?? `#${id}`;

  return isCurrent ? (
    <Text size="small" color="subtle">
      {t('metric_shared_current_workstream', {name}).toString()}
    </Text>
  ) : (
    <Link to={getWorkstreamDetailPath({id})} monochromatic>
      <Text size="small">{name}</Text>
    </Link>
  );
};

/** "Shared across N" under a shared metric's name; hover or click lists them. */
export const SharedMetricWorkstreams = ({
  workstreamIds,
  currentWorkstreamId,
}: {
  workstreamIds: number[];
  currentWorkstreamId?: number;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover triggerStrategy="hover" open={open} onToggle={setOpen} placement="bottom-start">
      <Popover.Trigger asChild>
        <button
          type="button"
          className={styles.redesignMetricSharedTrigger}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            setOpen(true);
          }}
        >
          <Text size="xsmall" color="subtle">
            {t('metric_shared_across_workstreams', {count: workstreamIds.length}).toString()}
          </Text>
        </button>
      </Popover.Trigger>
      <Popover.Overlay>
        <Popover.Content>
          <Popover.Body>
            <Stack gap="8" className={styles.redesignMetricSharedList}>
              {open
                ? workstreamIds.map((id) => (
                    <SharedMetricWorkstreamRow
                      key={id}
                      id={id}
                      isCurrent={id === currentWorkstreamId}
                    />
                  ))
                : null}
            </Stack>
          </Popover.Body>
        </Popover.Content>
      </Popover.Overlay>
    </Popover>
  );
};
